import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { House, Search, SquarePlus, CircleUserRound, ShoppingBag, Shirt, Receipt, Briefcase } from 'lucide-react';
import { RootState, AppDispatch } from '../../store';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useAccountType } from '../../hooks/useAccountType';
import BottomSheetModal from '../shared/BottomSheetModal';
import CreatorsStudioModal from '../studio/CreatorsStudioModal';
import FittingModal from '../studio/FittingModal';
import ArchiveUploadModal from '../archive/ArchiveUploadModal';
import { ReferralModal, ReferralBottomSheet } from '../referral';
import PurchaseFitCoinsModal from '../fitcoins/PurchaseFitCoinsModal';
import { fetchMyArchive } from '../../store/slices/archiveSlice';
import { useAuthModal } from '../../contexts/AuthModalContext';

const MobileBottomNav = () => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);
  const balanceData = useSelector((state: RootState) => state.tryOn.balanceData);
  const { openLoginModal } = useAuthModal();
  const [showStudioModal, setShowStudioModal] = useState(false);
  const [showFittingModal, setShowFittingModal] = useState(false);
  const [showArchiveUploadModal, setShowArchiveUploadModal] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  // Fitting modal state
  const [physicsEnabled, setPhysicsEnabled] = useState(true);
  const [showTracking, setShowTracking] = useState(false);
  const [environment, setEnvironment] = useState<'studio' | 'daylight' | 'evening' | 'runway'>('studio');
  
  // Daily FitCoin bonuses state
  const [dailyFitCoinsUsed] = useState(3);
  const dailyFitCoinsLimit = 5;
  
  // Referral state
  const [copied, setCopied] = useState(false);
  const referralUrl = `${window.location.origin}/signup?ref=${user?.id || 'user'}`;

  const handleCopyReferralLink = () => {
    navigator.clipboard.writeText(referralUrl);
    setCopied(true);
    toast.success('Referral link copied! 🎉', {
      style: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
      },
    });
    setTimeout(() => setCopied(false), 2000);
  };

  // Generate default avatar with user initials
  const getDefaultAvatar = () => {
    if (!user?.fullName) return null;
    const initials = user.fullName
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
    return initials;
  };

  // Check if user is on Creators Studio page
  const isOnCreatorsStudio = location.pathname === '/creators-studio';

  // The user's own social profile. Prefer the username (clean URL) and fall
  // back to the id when no username is set yet. The sidebar (Settings, Payments,
  // Help, Logout) is reached from the menu button on the profile page's header.
  const profilePath = `/social-profile/${user?.username || user?.id || ''}`;

  // Single source of truth for what each account type can do (`useAccountType`
  // returns flags; backend `user.features` can flip any flag off at runtime).
  // Buyers shop and request custom work — no posting, no studio.
  // Other surfaces (Settings, Payments, Help, Logout) live behind "More",
  // which simply opens the existing mobile sidebar drawer.
  const { isBuyer, flags } = useAccountType();

  const navItems: Array<{
    path: string;
    // Accept either a lucide icon (ForwardRefExoticComponent) or a plain
    // function component that takes className/SVG attrs (our MessagesIcon).
    icon: React.ElementType;
    label: string;
    isModal?: boolean;
    modalType?: string;
    badge?: number;
  }> = !isAuthenticated
    ? [
        { path: '/', icon: House, label: 'Home' },
        { path: '/archives', icon: Search, label: 'Explore' },
        { path: 'login', icon: CircleUserRound, label: 'Login', isModal: true, modalType: 'login' },
      ]
    : isBuyer
    ? [
        { path: '/', icon: House, label: 'Home' },
        { path: '/archives', icon: Search, label: 'Archives' },
        { path: '/orders', icon: Receipt, label: 'Orders' },
        { path: profilePath, icon: CircleUserRound, label: 'Profile' },
      ]
    : [
        { path: '/', icon: House, label: 'Home' },
        { path: '/archives', icon: Search, label: 'Archives' },
        // "Create" (post work) is a designer-only surface — gated on the
        // posting capability so buyers and logged-out users never see it.
        ...(flags.canPostArchive ? [
          { path: 'create', icon: SquarePlus, label: 'Create', isModal: true, modalType: 'archive' }
        ] : []),
        { path: '/orders', icon: Briefcase, label: 'Orders' },
        ...(isOnCreatorsStudio && flags.canUseCreatorsStudio ? [
          { path: 'studio', icon: ShoppingBag, label: 'Try-on', isModal: true, modalType: 'studio' },
          { path: 'fitting', icon: Shirt, label: 'Fitting', isModal: true, modalType: 'fitting' }
        ] : []),
        { path: profilePath, icon: CircleUserRound, label: 'Profile' },
      ];

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    // Modal items (create, studio, fitting) are never marked as active
    if (path === 'create' || path === 'studio' || path === 'fitting') {
      return false;
    }
    // Special handling for profile - active on social-profile or settings
    if (path.includes('/social-profile/')) {
      return location.pathname.includes('/social-profile/') || location.pathname.startsWith('/account/settings');
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (item: any, e: React.MouseEvent) => {
    if (item.isModal) {
      e.preventDefault();
      if (item.modalType === 'studio') {
        setShowStudioModal(true);
      } else if (item.modalType === 'fitting') {
        setShowFittingModal(true);
      } else if (item.modalType === 'archive') {
        setShowArchiveUploadModal(true);
      } else if (item.modalType === 'login') {
        openLoginModal();
      } else if (item.modalType === 'sidebar') {
        // Layout listens for this and opens the mobile sidebar drawer.
        window.dispatchEvent(new CustomEvent('ca:openSidebar'));
      }
    }
  };

  const handleArchiveUpload = async () => {
    // Check if we're on a profile page
    const profileMatch = location.pathname.match(/\/social-profile\/(.+)/);
    
    if (profileMatch) {
      // On a profile page - dispatch custom event to notify the page to refresh
      window.dispatchEvent(new CustomEvent('archiveCreated'));
    } else {
      // Not on a profile page - refresh user's own archive
      dispatch(fetchMyArchive({ page: 1, limit: 20 }));
    }
    
    setShowArchiveUploadModal(false);
  };

  // Check if we have more than 4 items for scrollable layout
  const hasMoreThanFourItems = navItems.length > 4;

  return (
    <>
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-gray-200/50 safe-area-bottom shadow-[0_-2px_10px_rgba(0,0,0,0.03)]">
      {hasMoreThanFourItems ? (
        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex items-center h-16 px-4 gap-2 min-w-max">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              
              // Special rendering for Profile icon when authenticated
              const isProfileIcon = item.label === 'Profile' && isAuthenticated;
              
              return (
                <Link
                  key={`${item.path}-${item.label}`}
                  to={item.isModal ? '#' : item.path}
                  onClick={(e) => handleNavClick(item, e)}
                  className="flex flex-col items-center justify-center min-w-[72px] h-full group relative px-3"
                >
                {/* Active indicator */}
                {active && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-gray-900 rounded-full" />
                )}
                
                <div className={`flex flex-col items-center justify-center transition-all duration-300 ease-out ${
                  active ? 'scale-105 -translate-y-0.5' : 'scale-100'
                }`}>
                  <div className={`relative transition-all duration-300 ${
                    active ? 'mb-1' : 'mb-0.5'
                  }`}>
                    {isProfileIcon ? (
                      // Show user profile image or default avatar
                      user?.profilePicture ? (
                        <img 
                          src={user.profilePicture} 
                          alt={user.fullName || 'Profile'}
                          className={`h-6 w-6 rounded-full object-cover transition-all duration-300 ${
                            active ? 'ring-2 ring-gray-900 ring-offset-1' : ''
                          }`}
                        />
                      ) : (
                        <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold transition-all duration-300 ${
                          active 
                            ? 'bg-gray-900 text-white' 
                            : 'bg-gray-200 text-gray-600 group-hover:bg-gray-300'
                        }`}>
                          {getDefaultAvatar() || 'U'}
                        </div>
                      )
                    ) : (
                      <>
                        <Icon 
                          className={`h-6 w-6 transition-all duration-300 ${
                            active 
                              ? 'text-gray-900 stroke-[2.5]' 
                              : 'text-gray-400 group-hover:text-gray-600 group-active:scale-90 stroke-[1.5]'
                          }`}
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        {item.badge !== undefined && item.badge > 0 && (
                          <span className="absolute -top-1 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#E9B306] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                            {item.badge > 9 ? '9+' : item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <span className={`text-[10px] font-medium transition-all duration-300 whitespace-nowrap ${
                    active 
                      ? 'text-gray-900 opacity-100' 
                      : 'text-gray-400 opacity-80 group-hover:text-gray-600 group-hover:opacity-100'
                  }`}>
                    {item.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
      ) : (
        <div className="flex items-center justify-around h-16 px-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            
            // Special rendering for Profile icon when authenticated
            const isProfileIcon = item.label === 'Profile' && isAuthenticated;
            
            return (
              <Link
                key={`${item.path}-${item.label}`}
                to={item.isModal ? '#' : item.path}
                onClick={(e) => handleNavClick(item, e)}
                className="flex flex-col items-center justify-center flex-1 h-full group relative"
              >
                {/* Active indicator */}
                {active && (
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-12 h-0.5 bg-gray-900 rounded-full" />
                )}
                
                <div className={`flex flex-col items-center justify-center transition-all duration-300 ease-out ${
                  active ? 'scale-105 -translate-y-0.5' : 'scale-100'
                }`}>
                  <div className={`relative transition-all duration-300 ${
                    active ? 'mb-1' : 'mb-0.5'
                  }`}>
                    {isProfileIcon ? (
                      // Show user profile image or default avatar
                      user?.profilePicture ? (
                        <img 
                          src={user.profilePicture} 
                          alt={user.fullName || 'Profile'}
                          className={`h-6 w-6 rounded-full object-cover transition-all duration-300 ${
                            active ? 'ring-2 ring-gray-900 ring-offset-1' : ''
                          }`}
                        />
                      ) : (
                        <div className={`h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-semibold transition-all duration-300 ${
                          active 
                            ? 'bg-gray-900 text-white' 
                            : 'bg-gray-200 text-gray-600 group-hover:bg-gray-300'
                        }`}>
                          {getDefaultAvatar() || 'U'}
                        </div>
                      )
                    ) : (
                      <>
                        <Icon 
                          className={`h-6 w-6 transition-all duration-300 ${
                            active 
                              ? 'text-gray-900 stroke-[2.5]' 
                              : 'text-gray-400 group-hover:text-gray-600 group-active:scale-90 stroke-[1.5]'
                          }`}
                          fill="none"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        {item.badge !== undefined && item.badge > 0 && (
                          <span className="absolute -top-1 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#E9B306] text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white">
                            {item.badge > 9 ? '9+' : item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  <span className={`text-[10px] font-medium transition-all duration-300 ${
                    active 
                      ? 'text-gray-900 opacity-100' 
                      : 'text-gray-400 opacity-80 group-hover:text-gray-600 group-hover:opacity-100'
                  }`}>
                    {item.label}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Studio Modal */}
      <BottomSheetModal
        isOpen={showStudioModal}
        onClose={() => setShowStudioModal(false)}
        title="Explore Outfits"
        maxHeight="max-h-[90vh]"
      >
        <CreatorsStudioModal
          isOpen={showStudioModal}
          onClose={() => setShowStudioModal(false)}
        />
      </BottomSheetModal>

      {/* Fitting Modal */}
      <BottomSheetModal
        isOpen={showFittingModal}
        onClose={() => setShowFittingModal(false)}
        title="Virtual Fitting"
        maxHeight="max-h-[90vh]"
      >
        <FittingModal
          isOpen={showFittingModal}
          onClose={() => setShowFittingModal(false)}
          physicsEnabled={physicsEnabled}
          setPhysicsEnabled={setPhysicsEnabled}
          showTracking={showTracking}
          setShowTracking={setShowTracking}
          environment={environment}
          setEnvironment={setEnvironment}
          onShowReferralModal={() => setShowReferralModal(true)}
          onShowPurchaseModal={() => setShowPurchaseModal(true)}
          balanceData={balanceData}
          dailyFitCoinsUsed={dailyFitCoinsUsed}
          dailyFitCoinsLimit={dailyFitCoinsLimit}
        />
      </BottomSheetModal>

    </nav>
    
    {/* Archive Upload Modal - Outside nav to avoid z-index issues */}
    <ArchiveUploadModal
      isOpen={showArchiveUploadModal}
      onClose={() => setShowArchiveUploadModal(false)}
      onUpload={handleArchiveUpload}
    />

    {/* Purchase FitCoins Modal */}
    <PurchaseFitCoinsModal
      isOpen={showPurchaseModal}
      onClose={() => setShowPurchaseModal(false)}
      currentBalance={0}
      onPurchase={(amount, price) => {
        console.log('Purchase:', amount, 'coins for', price);
        toast.success('Purchase initiated! Check WhatsApp for details.');
      }}
    />

    {/* Referral Modals */}
    <ReferralModal
      isOpen={showReferralModal}
      onClose={() => setShowReferralModal(false)}
      referralUrl={referralUrl}
      copied={copied}
      onCopy={handleCopyReferralLink}
    />

    <ReferralBottomSheet
      isOpen={showReferralModal}
      onClose={() => setShowReferralModal(false)}
      referralUrl={referralUrl}
      copied={copied}
      onCopy={handleCopyReferralLink}
    />
    </>
  );
};

export default MobileBottomNav;
